"use client";

import type { ChangeEvent, FormEvent } from "react";
import MarkerPinIcon from "../icons/MarkerPinIcon";
import type { Store } from "@/lib/types/stores";
import { useScrollLock } from "@/lib/use-scroll-lock";
import { Button, ModalCloseButton, ModalPortal } from "@/components";

interface LocationModalProps {
  isOpen: boolean;
  mode: "pickup" | "delivery";
  postalCode: string;
  postalError: string;
  loading: boolean;
  currentLocation: Store | null;
  newLocation: Store | null;
  stores?: Store[];
  selectedLocationId?: number | null;
  onClose: () => void;
  onPostalCodeChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onPostalCodeSubmit: (e: FormEvent) => void;
  onLocationChange: (locationId: number) => void;
  onUseLocation: () => void;
  onGeolocationError?: (error: GeolocationPositionError) => void;
}

export default function LocationModal({
  isOpen,
  mode,
  postalCode,
  postalError,
  loading,
  currentLocation,
  newLocation,
  stores = [],
  selectedLocationId,
  onClose,
  onPostalCodeChange,
  onPostalCodeSubmit,
  onLocationChange,
  onUseLocation,
  onGeolocationError,
}: LocationModalProps) {
  useScrollLock(isOpen);

  if (!isOpen) return null;

  // ── Store List Modal (Pickup – no newLocation yet) ──────────────────────────
  if (mode === "pickup" && !newLocation && stores.length > 0) {
    return (
      <ModalPortal>
      <div
        className="fixed inset-0 z-[1000] flex items-end justify-center bg-[#07063CBF] p-4 sm:items-center"
        onClick={onClose}
      >
        <div
          className="relative flex w-full max-w-sm flex-col overflow-hidden rounded-2xl bg-constant-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-10 px-5 py-4">
            <h3 className="text-xl font-bold text-text-primary">
              Select store
            </h3>
            <ModalCloseButton
              onClick={onClose}
              className="absolute right-4 top-4 z-10"
              aria-label="Close"
            />
          </div>

          {/* Store list */}
          <div className="flex-1 overflow-y-auto">
            {stores.map((store, idx) => {
              const isSelected = selectedLocationId === store.covaLocationId;
              return (
                <Button
                  raw
                  key={store.covaLocationId}
                  type="button"
                  onClick={() => onLocationChange(store.covaLocationId)}
                  className={`flex w-full items-center justify-between px-5 py-3.5 text-left transition-colors ${
                    idx !== stores.length - 1 ? "border-b border-neutral-10" : ""
                  } ${
                    isSelected
                      ? "bg-blue-5"
                      : "hover:bg-bg-1"
                  }`}
                >
                  <div className="flex flex-col">
                    <span className={`text-sm font-semibold ${isSelected ? "text-blue-100" : "text-text-primary"}`}>
                      {store.name}
                    </span>
                    {store.city && (
                      <span className="mt-0.5 text-xs text-text-secondary">
                        {store.city}{store.province ? `, ${store.province}` : ""}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 text-blue-100">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
      </ModalPortal>
    );
  }

  // ── Change Store Confirmation Modal (Pickup – newLocation chosen) ───────────
  if (mode === "pickup" && currentLocation && newLocation) {
    return (
      <ModalPortal>
      <div
        className="fixed inset-0 z-[1000] flex items-end justify-center bg-[#07063CBF] p-4 sm:items-center"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-constant-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-10 px-5 py-5">
            <h3 className="text-lg font-bold text-text-primary">
              Change store?
            </h3>
            <ModalCloseButton
              onClick={onClose}
              className="absolute right-5 top-5 z-10"
              aria-label="Close"
            />
          </div>

          {/* Body */}
          <div className="space-y-3 px-5 py-5">
            <div className="flex items-start gap-3 rounded-xl border border-neutral-10 bg-bg-1 p-3.5">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-neutral-10">
                <MarkerPinIcon size={16} color="currentColor" className="text-text-secondary" />
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-text-tertiary">Current store</p>
                <p className="mt-0.5 text-sm font-semibold text-text-primary">
                  {currentLocation.name}
                  {currentLocation.city ? `, ${currentLocation.city}` : ""}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-neutral-40">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-blue-10 bg-blue-5 p-3.5">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-10">
                <MarkerPinIcon size={16} color="currentColor" className="text-blue-100" />
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-blue-80">New store</p>
                <p className="mt-0.5 text-sm font-semibold text-blue-100">
                  {newLocation.name}
                  {newLocation.city ? `, ${newLocation.city}` : ""}
                </p>
              </div>
            </div>

            <p className="text-xs text-text-tertiary">
              Your cart may be cleared when changing stores.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 border-t border-neutral-10 px-5 py-4">
            <Button
              variant="bordered"
              size="md"
              onClick={onClose}
              className="rounded-xl py-2.5"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => onLocationChange(newLocation.covaLocationId)}
              className="flex-1 rounded-xl"
            >
              Switch store
            </Button>
          </div>
        </div>
      </div>
      </ModalPortal>
    );
  }

  // ── Delivery Location Modal ─────────────────────────────────────────────────
  return (
    <ModalPortal>
    <div
      className="fixed inset-0 z-[1000] flex items-end justify-center bg-[#07063CBF] p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-constant-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-10 px-5 py-4 relative">
          <div>
            <h3 className="text-base font-bold text-text-primary">
              Delivery location
            </h3>
            <p className="mt-0.5 text-xs text-text-secondary">
              Enter your postal code to check delivery availability
            </p>
          </div>
          <ModalCloseButton
            onClick={onClose}
            className="absolute right-3 top-3 z-10"
            aria-label="Close"
          />
        </div>

        {/* Form */}
        <div className="px-5 py-5 space-y-4">
          <form onSubmit={onPostalCodeSubmit} className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={postalCode}
                onChange={onPostalCodeChange}
                placeholder="A1A 1A1"
                maxLength={7}
                autoFocus
                className="h-10 min-w-0 flex-1 rounded-xl border border-neutral-10 px-3 text-center text-sm font-semibold uppercase tracking-widest text-text-primary placeholder:text-neutral-40 placeholder:normal-case placeholder:tracking-normal focus:border-blue-60 focus:outline-none focus:ring-2 focus:ring-blue-80/20"
              />
              <Button
                type="submit"
                disabled={loading || !postalCode.trim()}
                variant="solid"
                color="primary"
                size="md"
                className="h-10 shrink-0 rounded-xl px-4"
              >
                {loading ? (
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : "Confirm"}
              </Button>
            </div>
            {postalError && (
              <p className="flex items-center gap-1.5 text-xs font-medium text-red-60">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {postalError}
              </p>
            )}
          </form>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-neutral-10" />
            <span className="text-xs font-medium text-text-tertiary">or</span>
            <div className="h-px flex-1 bg-neutral-10" />
          </div>

          <Button
            variant="solid"
            color="primary"
            size="lg"
            startIcon={<MarkerPinIcon size={18} color="currentColor" className="shrink-0 text-current" />}
            onClick={async () => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                  async (_position) => { onUseLocation(); },
                  (error) => { if (onGeolocationError) onGeolocationError(error); }
                );
              } else {
                if (onGeolocationError) {
                  const error = new Error(
                    "Geolocation is not supported by your browser."
                  ) as unknown as GeolocationPositionError;
                  onGeolocationError(error);
                }
              }
            }}
            className="w-full rounded-xl"
          >
            Use my location
          </Button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}
